import { Breadcrumb, Button, Card, Col, DatePicker, Empty, message, Modal, Row, Select, Table } from 'antd';
import gql from 'graphql-tag';
import * as moment from 'moment';
import React, { Component } from 'react';
import { Mutation, MutationFn, Query } from 'react-apollo';
import { Link, RouteComponentProps } from 'react-router-dom';
import { DateRange } from 'src/types/base';

import {
  MarketingActivityRuleKey,
  MarketingActivityRuleType,
  ReservationCommonSchema,
  RuleUserSkuMapping,
} from '../../types/marketingActivity';

const RESERVE_C39X_DETAIL_RULE_NAME = '详情';
const RESERVE_C39X_SKU_RULE_NAME = 'SKU';

export const skuListQuery = gql`
  query getSkuListQuery {
    skus {
      id
      name
      course {
        id
        serialId
      }
    }
  }
`;

const skuGroupListQuery = gql`
  query {
    skuGroups {
      id
      name
    }
  }
`;

const createMarketingActivityRuleMutation = gql`
  mutation createMarketingActivityRule($activityId: Int, $rules: [JSON]) {
    createMarketingActivityRule(activityId: $activityId, rules: $rules) {
      id
    }
  }
`;

interface SelectCourse {
  skuId: number;
  skuName: string;
}

interface State {
  reserveStartEndRange: DateRange;
  enrollStartEndRange: DateRange;
  selectedCurrentCourse: SelectCourse;
  selectedRecommendCourse: SelectCourse;
  selectedSkuGroup: string;
  isConfirmModalVisible: boolean;
  skuGroups: {
    id: number;
    name: string;
  }[];
  dataSource: {
    key: number;
    currentCourseSkuName: string;
    currentCourseSkuId: number;
    recommendCourseSkuName: string;
    recommendCourseSkuId: number;
  }[];
}

export class ReserveActivityRule extends Component<RouteComponentProps<{ id: string }>, State> {
  constructor(props: RouteComponentProps<{ id: string }>) {
    super(props);

    this.state = {
      selectedSkuGroup: '',
      reserveStartEndRange: { from: new Date(), to: new Date() },
      enrollStartEndRange: { from: new Date(), to: new Date() },
      selectedCurrentCourse: {} as SelectCourse,
      selectedRecommendCourse: {} as SelectCourse,
      isConfirmModalVisible: false,
      dataSource: [],
      skuGroups: [],
    };
  }

  getSkuGroups() {
    return (
      <Query query={skuGroupListQuery}>
        {({ loading, error, data }) => {
          if (error) return <div>{error.message}</div>;
          if (loading || !data) return <div>loading...</div>;
          if (!data) {
            // @ts-ignore
            return <Empty />;
          }
          const { skuGroups } = data;

          return (
            <Select
              placeholder="选择skuGroup"
              style={{ width: '65%' }}
              onChange={(value: string) => {
                this.setState({
                  selectedSkuGroup: value,
                });
              }}
            >
              {skuGroups.map((skuGroup: { id: string; name: string }) => {
                return <Select.Option key={skuGroup.id}>{skuGroup.name} </Select.Option>;
              })}
            </Select>
          );
        }}
      </Query>
    );
  }

  renderCoursesSelect() {
    return (
      <Query query={skuListQuery}>
        {({ loading, error, data }) => {
          if (error) return <div>{error.message}</div>;
          if (loading) return <div>loading...</div>;
          if (!data) {
            // @ts-ignore
            return <Empty />;
          }
          const { skus } = data;

          return (
            <>
              <Col span={5}>
                <span style={{ marginRight: '3px' }}>当前课程：</span>
                <Select
                  placeholder="当前课程"
                  style={{ width: '50%' }}
                  onChange={(value: number, option: React.ReactElement<any>) => {
                    this.setState({
                      selectedCurrentCourse: {
                        skuId: value,
                        skuName: option.props.children,
                      },
                    });
                  }}
                >
                  {skus.map((sku: { id: number; name: string }) => {
                    return (
                      <Select.Option key={sku.id} value={sku.id}>
                        {sku.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Col>

              <Col span={5}>
                <span style={{ marginRight: '3px' }}>推荐课程：</span>
                <Select
                  placeholder="大课课程"
                  style={{ width: '50%' }}
                  onChange={(value: number, option: React.ReactElement<any>) => {
                    this.setState({
                      selectedRecommendCourse: {
                        skuId: value,
                        skuName: option.props.children,
                      },
                    });
                  }}
                >
                  {skus.map((sku: { id: number; name: string }) => {
                    return (
                      <Select.Option key={sku.id} value={sku.id}>
                        {sku.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Col>
            </>
          );
        }}
      </Query>
    );
  }

  async onConfirmModalHandleOk(
    createMarketingActivityRuleMutation: MutationFn<
      any,
      {
        activityId: number;
        rules: {
          ruleKey: MarketingActivityRuleKey;
          ruleName: string;
          ruleType: number;
          ruleValue: ReservationCommonSchema | RuleUserSkuMapping;
        }[];
      }
    >,
  ) {
    const { reserveStartEndRange, enrollStartEndRange, selectedSkuGroup } = this.state;

    this.setState({ isConfirmModalVisible: false });

    if (moment(reserveStartEndRange.from).valueOf() > moment(reserveStartEndRange.to).valueOf()) {
      message.error('预约开始时间不能大于预约结束时间');
      return;
    }

    if (moment(enrollStartEndRange.from).valueOf() > moment(enrollStartEndRange.to).valueOf()) {
      message.error('开始报名时间不能大于报名截止时间');
      return;
    }

    if (moment(enrollStartEndRange.from).valueOf() > moment(enrollStartEndRange.to).valueOf()) {
      message.error('预约开始时间不能大于报名结束时间');
      return;
    }

    if (!selectedSkuGroup) {
      message.error('请选择skuGroup');
      return;
    }

    try {
      await createMarketingActivityRuleMutation();
      message.success(' 新增成功！');
    } catch {
      message.error('新增失败！');
    }
  }

  onAddCourseConfigClick() {
    const { selectedCurrentCourse, selectedRecommendCourse, dataSource } = this.state;

    if (!selectedCurrentCourse.skuId || !selectedRecommendCourse.skuId) {
      message.error('请选择课程！');
      return;
    }

    dataSource.push({
      key: selectedCurrentCourse.skuId,
      currentCourseSkuName: selectedCurrentCourse.skuName,
      currentCourseSkuId: selectedCurrentCourse.skuId,
      recommendCourseSkuName: selectedRecommendCourse.skuName,
      recommendCourseSkuId: selectedRecommendCourse.skuId,
    });

    this.setState({
      dataSource,
    });
  }

  render() {
    const { reserveStartEndRange, enrollStartEndRange, selectedSkuGroup, dataSource } = this.state;

    const reservationCommonSchema = {
      reservationStartAt: new Date(moment(reserveStartEndRange.from).utc().format()),
      reservationEndAt: new Date(moment(reserveStartEndRange.to).utc().format()),
      saleStartAt: new Date(moment(enrollStartEndRange.from).utc().format()),
      saleEndAt: new Date(moment(enrollStartEndRange.to).utc().format()),
      skuGroupName: selectedSkuGroup,
    };

    const rules: {
      ruleKey: MarketingActivityRuleKey;
      ruleName: string;
      ruleType: MarketingActivityRuleType;
      ruleValue: ReservationCommonSchema | RuleUserSkuMapping;
    }[] = [
      {
        ruleKey: MarketingActivityRuleKey.DETAIL_RESERVATION_COMMON,
        ruleName: RESERVE_C39X_DETAIL_RULE_NAME,
        ruleType: MarketingActivityRuleType.DETAIL,
        ruleValue: reservationCommonSchema,
      },
    ];

    const skuRules = dataSource.map(item => {
      return {
        ruleKey: MarketingActivityRuleKey.USER_SKU_MAPPING,
        ruleName: RESERVE_C39X_SKU_RULE_NAME,
        ruleType: MarketingActivityRuleType.DETAIL,
        ruleValue: {
          ownedSkuId: item.currentCourseSkuId,
          skuId: item.recommendCourseSkuId,
        },
      };
    });
    rules.push(...skuRules);

    return (
      <>
        <Breadcrumb style={{ marginBottom: 10, fontSize: 13 }}>
          <Breadcrumb.Item>
            <Link to={`/Home`}>首页</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to={`/marketing`}>营销活动</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>规则配置</Breadcrumb.Item>
        </Breadcrumb>
        <Card>
          <Row gutter={24}>
            <Col span={3} style={{ marginTop: '6px', textAlign: 'right' }}>
              选择SkuGroup：
            </Col>
            <Col span={9}>{this.getSkuGroups()}</Col>
          </Row>

          <Row gutter={24} style={{ marginTop: '10px' }}>
            <Col span={3} style={{ marginTop: '6px', textAlign: 'right' }}>
              预约时间：
            </Col>
            <Col span={6}>
              <DatePicker.RangePicker
                value={[moment(reserveStartEndRange.from), moment(reserveStartEndRange.to)]}
                onChange={dates =>
                  this.setState({
                    reserveStartEndRange: {
                      from: moment(dates[0]).startOf('day').toDate(),
                      to: moment(dates[1]).endOf('day').toDate(),
                    },
                  })
                }
              />
            </Col>
          </Row>

          <Row gutter={24} style={{ marginTop: '10px' }}>
            <Col span={3} style={{ marginTop: '6px', textAlign: 'right' }}>
              报名时间：
            </Col>
            <Col span={6}>
              <DatePicker.RangePicker
                value={[moment(enrollStartEndRange.from), moment(enrollStartEndRange.to)]}
                onChange={dates =>
                  this.setState({
                    enrollStartEndRange: {
                      from: moment(dates[0]).startOf('day').toDate(),
                      to: moment(dates[1]).endOf('day').toDate(),
                    },
                  })
                }
              />
            </Col>
          </Row>

          <Row gutter={24} style={{ marginTop: '10px' }}>
            <Col span={3} style={{ marginTop: '6px', textAlign: 'right' }}>
              课程设置：
            </Col>
            {this.renderCoursesSelect()}
            <Col span={3}>
              <Button
                type="primary"
                onClick={() => {
                  this.onAddCourseConfigClick();
                }}
              >
                添加
              </Button>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12} style={{ textAlign: 'right' }} offset={3}>
              <Table
                size="small"
                columns={[
                  {
                    title: '当前课程',
                    key: 'currentCourseSkuName',
                    dataIndex: 'currentCourseSkuName',
                  },
                  {
                    title: '推荐课程',
                    key: 'recommendCourseSkuName',
                    dataIndex: 'recommendCourseSkuName',
                  },
                ]}
                dataSource={dataSource}
                style={{ marginTop: '10px' }}
              />
            </Col>
          </Row>

          <Row gutter={24} style={{ marginTop: '10px' }}>
            <Col span={3} style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                onClick={() => {
                  this.setState({
                    isConfirmModalVisible: true,
                  });
                }}
              >
                确认添加
              </Button>
            </Col>
            <Mutation
              mutation={createMarketingActivityRuleMutation}
              variables={{
                rules,
                activityId: Number(this.props.match.params.id),
              }}
            >
              {createActivityRecordMutation => {
                return (
                  <Modal
                    title="Basic Modal"
                    visible={this.state.isConfirmModalVisible}
                    onCancel={() => {
                      this.setState({
                        isConfirmModalVisible: false,
                      });
                    }}
                    onOk={async () => {
                      await this.onConfirmModalHandleOk(createActivityRecordMutation);
                    }}
                  >
                    确认添加吗？
                  </Modal>
                );
              }}
            </Mutation>
          </Row>
        </Card>
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/marketing/ReserveRule.tsx